import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Books, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { dmsApi, fmtDate } from './dmsApi';

/** ビューアーから「この書類をバインダーに入れる」。既存に足す／新しく作る の2択だけ。 */
export function AddToBundleDialog({ open, documentId, onClose, onDone }) {
  const [bundles, setBundles] = useState([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    dmsApi.bundles().then(setBundles).catch(() => setBundles([]));
  }, [open]);

  const addTo = async (b) => {
    setBusy(true);
    try {
      const detail = await dmsApi.bundle(b.id);
      const ids = detail.documents.map((d) => d.id);
      if (ids.includes(documentId)) { toast.info('すでにこのバインダーに入っています'); return; }
      await dmsApi.updateBundle(b.id, { document_ids: [...ids, documentId] });
      toast.success(`「${b.name}」に追加しました`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || '追加に失敗しました'); }
    finally { setBusy(false); }
  };

  const createNew = async () => {
    if (!name.trim()) { toast.error('バインダー名を入力してください'); return; }
    setBusy(true);
    try {
      await dmsApi.createBundle({ name: name.trim(), document_ids: [documentId] });
      toast.success(`バインダー「${name.trim()}」を作成しました`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || '作成に失敗しました'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="add-to-bundle-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Books size={18} className="text-amber-600" /> バインダーに追加
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {bundles.length === 0 && <p className="text-xs text-zinc-400 py-4 text-center">既存のバインダーはありません</p>}
          {bundles.map((b) => (
            <button key={b.id} type="button" disabled={busy} onClick={() => addTo(b)}
              className="w-full flex items-center gap-2 rounded-md border border-zinc-200 px-2 py-1.5 text-left hover:border-amber-400 hover:bg-amber-50 transition-colors disabled:opacity-50"
              data-testid={`add-to-bundle-${b.id}`}>
              <Books size={15} weight="duotone" className="text-amber-600 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-zinc-800 truncate">{b.name}</span>
                <span className="block text-[10px] text-zinc-400">{b.member_count}件 · {fmtDate(b.updated_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="border-t border-zinc-200 pt-3 space-y-1.5">
          <p className="text-[11px] font-semibold text-zinc-600">新しいバインダーを作る</p>
          <div className="flex items-center gap-1.5">
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="バインダー名" className="h-8 text-xs flex-1"
              data-testid="add-to-bundle-new-name"
              onKeyDown={(e) => e.key === 'Enter' && createNew()} />
            <Button size="sm" disabled={busy} onClick={createNew}
              className="h-8 bg-amber-600 hover:bg-amber-700 text-xs" data-testid="add-to-bundle-create">
              <Plus size={13} className="mr-0.5" /> 作成
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddToBundleDialog;
